/**
 * ÉTAT DE LA PERMISSION MICRO — module PUR (issue #190).
 *
 * Avant ce lot, le poste appelait `getUserMedia` au chargement sans jamais
 * savoir si le navigateur allait demander, accorder ou refuser. Sur Chromium de
 * bureau, un « Refuser » réflexe sur cette demande surprise est DÉFINITIF. Ce
 * module répond à trois questions, sans jamais toucher au micro lui-même :
 *
 *   1. où en est la permission ? (`watchMicPermission` → prompt / granted /
 *      denied / unknown, + abonnement à `onchange`) ;
 *   2. faut-il demander maintenant ou attendre un geste ? (`decideMicRequest`) ;
 *   3. cet appareil a-t-il déjà obtenu le micro ? (mémo `localStorage`).
 *
 * CONTRAT : aucune fonction d'ici ne jette, quel que soit le navigateur (API
 * absente, `query` qui jette en synchrone, qui rejette, ou qui ne répond
 * jamais). Le repli est toujours `unknown` — et c'est `getUserMedia` qui dit
 * alors la vérité, comme avant.
 */

export type MicPermissionState = 'prompt' | 'granted' | 'denied' | 'unknown'

/** Moteur du navigateur, au sens « que vaut un refus de la demande ? ». */
export type MicEngine = 'chromium' | 'webkit' | 'gecko' | 'other'

/** Ce que le poste fait de l'état lu, AVANT tout `getUserMedia`. */
export type MicRequestDecision = 'request-now' | 'await-gesture'

export interface MicPermissionWatch {
  /** État COURANT (relu sur l'objet vivant du navigateur, pas une photo). */
  read(): MicPermissionState
  /** Désabonne `onchange`. Idempotent. */
  stop(): void
}

/** Ce qu'on attend de `navigator` — injectable pour les specs. */
export interface MicPermissionNavigator {
  permissions?: {
    query?: (descriptor: { name: string }) => Promise<MicPermissionStatusLike>
  }
}

interface MicPermissionStatusLike {
  state?: string
  onchange?: ((event?: unknown) => void) | null
  addEventListener?: (type: 'change', listener: () => void) => void
  removeEventListener?: (type: 'change', listener: () => void) => void
}

/**
 * Attente BORNÉE de `permissions.query` : certaines WebViews ne répondent
 * jamais. Sans cette borne, l'initialisation du poste resterait suspendue à une
 * promesse morte. Ce n'est pas un retry : passé le délai, l'état est `unknown`.
 */
export const MIC_PERMISSION_QUERY_TIMEOUT_MS = 1500

const NO_WATCH: MicPermissionWatch = {
  read: () => 'unknown',
  stop: () => undefined,
}

function normalize(state: unknown): MicPermissionState {
  return state === 'prompt' || state === 'granted' || state === 'denied'
    ? state
    : 'unknown'
}

function defaultNavigator(): MicPermissionNavigator | undefined {
  return typeof navigator === 'undefined'
    ? undefined
    : (navigator as unknown as MicPermissionNavigator)
}

/**
 * Lit la permission micro et s'abonne à ses changements.
 *
 * `onChange` reçoit le NOUVEL état à chaque `change` du navigateur (l'agent
 * vient d'autoriser dans les réglages du site, de bloquer, de réinitialiser).
 * Une exception de l'abonné est avalée : elle ne doit pas remonter dans la
 * boucle d'événements du navigateur.
 *
 * Ne jette JAMAIS et ne rejette jamais : API absente, `query` qui jette ou
 * rejette (vieux Safari, Firefox ancien : `TypeError` sur `microphone`), ou qui
 * ne répond pas → veille inerte dont `read()` rend `unknown`.
 */
export async function watchMicPermission(
  onChange: (state: MicPermissionState) => void,
  nav: MicPermissionNavigator | undefined = defaultNavigator(),
): Promise<MicPermissionWatch> {
  let status: MicPermissionStatusLike | null = null
  try {
    const query = nav?.permissions?.query
    if (typeof query !== 'function') return NO_WATCH

    let timer: ReturnType<typeof setTimeout> | null = null
    const timeout = new Promise<null>((resolve) => {
      timer = setTimeout(() => resolve(null), MIC_PERMISSION_QUERY_TIMEOUT_MS)
    })
    try {
      status = await Promise.race([
        // `call` : `query` exige son `this` (Illegal invocation sinon).
        query.call(nav?.permissions, { name: 'microphone' }),
        timeout,
      ])
    } finally {
      if (timer) clearTimeout(timer)
    }
  } catch {
    return NO_WATCH
  }
  if (!status || typeof status !== 'object') return NO_WATCH

  const held = status
  const listener = () => {
    try {
      onChange(normalize(held.state))
    } catch (error) {
      console.warn('[telephony-v2] Abonné à la permission micro en erreur', error)
    }
  }

  let usedProperty = false
  try {
    if (typeof held.addEventListener === 'function') {
      held.addEventListener('change', listener)
    } else {
      held.onchange = listener
      usedProperty = true
    }
  } catch {
    // Pas d'abonnement possible : l'état reste lisible, la reprise passera par
    // le bouton « J'ai autorisé, réessayer ».
  }

  let stopped = false
  return {
    read: () => {
      try {
        return normalize(held.state)
      } catch {
        return 'unknown'
      }
    },
    stop: () => {
      if (stopped) return
      stopped = true
      try {
        if (usedProperty) held.onchange = null
        else held.removeEventListener?.('change', listener)
      } catch {
        // rien à retirer
      }
    },
  }
}

/**
 * DEMANDER MAINTENANT, OU ATTENDRE UN GESTE ?
 *
 * | État      | Décision                                                        |
 * |-----------|-----------------------------------------------------------------|
 * | `granted` | maintenant — comportement historique, aucune demande n'apparaît |
 * | `denied`  | maintenant — `getUserMedia` rejette aussitôt SANS rien afficher ; |
 * |           | c'est lui qui fait foi (une API de permission peut mentir)      |
 * | `prompt`  | geste — sauf WebKit ayant DÉJÀ obtenu le micro sur cet appareil |
 * | `unknown` | geste sur Chromium (refus définitif) ; maintenant ailleurs      |
 *
 * L'exception WebKit est le cas iPad : Safari répond `prompt` à CHAQUE session
 * (la permission n'y est pas persistée par défaut) et y refuser n'est pas
 * définitif. Imposer le pré-écran à chaque ouverture ajouterait un clic par
 * jour à chaque agent pour rien ; on ne le montre donc qu'au tout premier
 * usage, ou après un refus (le mémo est alors effacé).
 */
export function decideMicRequest(input: {
  permission: MicPermissionState
  engine: MicEngine
  grantedBefore: boolean
}): MicRequestDecision {
  switch (input.permission) {
    case 'granted':
    case 'denied':
      return 'request-now'
    case 'prompt':
      return input.engine === 'webkit' && input.grantedBefore
        ? 'request-now'
        : 'await-gesture'
    default:
      return input.engine === 'chromium' ? 'await-gesture' : 'request-now'
  }
}

// ---------------------------------------------------------------------------
// Mémo « cet appareil a déjà obtenu le micro »
// ---------------------------------------------------------------------------

/** Par appareil et par origine — comme la permission elle-même. */
export const MIC_GRANT_MEMO_KEY = 'telephony_v2_mic_granted'

function storage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage
  } catch {
    // Safari en navigation privée stricte : l'accès lui-même peut jeter.
    return null
  }
}

export function hasMicGrantMemo(): boolean {
  try {
    return storage()?.getItem(MIC_GRANT_MEMO_KEY) === '1'
  } catch {
    return false
  }
}

export function rememberMicGrant(): void {
  try {
    storage()?.setItem(MIC_GRANT_MEMO_KEY, '1')
  } catch {
    // quota / mode privé : le pré-écran réapparaîtra, rien de plus
  }
}

export function forgetMicGrant(): void {
  try {
    storage()?.removeItem(MIC_GRANT_MEMO_KEY)
  } catch {
    // idem
  }
}

// ---------------------------------------------------------------------------
// Mémo IMPLICITE — les appareils déjà en service le jour du déploiement
// ---------------------------------------------------------------------------

/**
 * LE PREMIER MATIN (recette du lot #190, D2). Le mémo ci-dessus n'existe que
 * depuis ce lot : au déploiement, AUCUN iPad ne le porte. Sans rattrapage,
 * tous les agents verraient le pré-écran le même matin — et ne recevraient
 * aucun entrant tant qu'ils n'auraient pas touché le bouton.
 *
 * Or un appareil qui a déjà servi au téléphone a DÉJÀ vu la demande de
 * Safari : jusqu'à ce lot, le poste appelait `getUserMedia` à chaque
 * chargement d'une session connectée. Pour lui, l'init directe est le
 * comportement d'hier, pas une surprise. On reconnaît un tel appareil à une
 * trace locale que seule l'activité d'appel écrit :
 *
 *  - `crm_missed_calls_v2` — écrite par `services/telephonyMissedCalls.ts`
 *    quand un entrant manqué est reçu, lu ou effacé : une session v2 a vécu
 *    ici pendant qu'un appel sonnait ;
 *  - `activeCampaignId` — écrite par `CampaignContext` UNIQUEMENT quand un
 *    agent choisit sa campagne à la main (l'auto-sélection n'écrit rien) :
 *    seul un AGENT a des campagnes, et il la choisit pour appeler ;
 *  - `autodial-settings` — réglage du rebours d'appel automatique (v1) ;
 *  - `call-notes-<contactId>` — notes prises pendant un appel (v1).
 *
 * Sont volontairement ÉCARTÉES : `accessToken` / `refreshToken` (présents dès
 * la connexion, donc sur un appareil neuf), `seenCallbackIds` et
 * `sidebar:pinned` (écrites à chaque chargement, téléphone ou non),
 * `telephony_v2` (posée par un administrateur, pas par un appel).
 *
 * UNE SEULE FOIS PAR APPAREIL : le verdict est figé par un marqueur au premier
 * passage. Sans lui, un appareil réellement neuf — qui choisirait une campagne
 * AVANT de toucher « Activer le micro » — se fabriquerait une trace après
 * coup et sauterait le pré-écran au chargement suivant.
 *
 * À n'appeler que pour WebKit : sur Chromium `permissions.query` fait foi, et
 * le mémo n'y est de toute façon jamais lu (`decideMicRequest`).
 *
 * @returns `true` si le mémo vient d'être posé par cette adoption.
 */
export const MIC_LEGACY_CHECK_KEY = 'telephony_v2_mic_legacy_checked'

const LEGACY_PHONE_USE_KEYS = ['crm_missed_calls_v2', 'activeCampaignId', 'autodial-settings']
const LEGACY_CALL_NOTES_PREFIX = 'call-notes-'

export function adoptLegacyMicGrant(): boolean {
  try {
    const store = storage()
    if (!store) return false
    if (store.getItem(MIC_LEGACY_CHECK_KEY) !== null) return false

    let used = LEGACY_PHONE_USE_KEYS.some((key) => store.getItem(key) !== null)
    for (let i = 0; !used && i < store.length; i += 1) {
      used = store.key(i)?.startsWith(LEGACY_CALL_NOTES_PREFIX) === true
    }

    // Marqueur D'ABORD : si l'écriture échoue (quota, mode privé), on renonce
    // — un verdict qu'on ne peut pas figer se rejouerait à chaque chargement.
    store.setItem(MIC_LEGACY_CHECK_KEY, '1')
    if (!used || store.getItem(MIC_GRANT_MEMO_KEY) === '1') return false
    store.setItem(MIC_GRANT_MEMO_KEY, '1')
    return true
  } catch {
    return false
  }
}
